import { Text, IconButton, ErrorBoundary, HStack, VStack } from '@genial/components';
import { Color, Spacing, Typography } from '@genial/design-tokens';
import { useBreakpointValue } from '@genial/utils';
import { Actionsheet, Box, Popover, useDisclose } from 'native-base';
import React, { useState } from 'react';

import { CustomerItemTagObject } from '../../../../utils/types';
import { SkeletonTags } from '../SkeletonTags';

import { ItemTagContent } from './content';
import { ItemTagTriggerButton } from './triggerButton';

export interface ItemTagProps {
  itemId: number;
  data: CustomerItemTagObject[];
  isListing?: boolean;
  errorTags?: Error;
  isLoadingTags?: boolean;
  refresherTags?: () => void;
}

export const ItemTag: React.FC<ItemTagProps> = ({
  data,
  itemId,
  isListing = false,
  errorTags,
  isLoadingTags = false,
  refresherTags = () => {},
}) => {
  const { isOpen, onOpen, onClose } = useDisclose();
  const isDesktop = useBreakpointValue({ base: false, lg: true });

  const [isOpenPopover, setIsOpenPopover] = useState(false);

  const handleActivePopover = () => setIsOpenPopover(true);

  const tagContentProps = {
    data,
    itemId,
    isListing,
    errorTags,
    isLoadingTags,
    refresherTags,
  };

  if (isDesktop) {
    return (
      <Popover
        useRNModal
        trigger={(triggerProps) => (
          <ItemTagTriggerButton
            onOpen={handleActivePopover}
            triggerProps={triggerProps}
            tags={data}
            isListing={isListing}
          />
        )}
        isOpen={isOpenPopover}
        onClose={() => setIsOpenPopover((state) => !state)}>
        <Popover.Content maxW="270px" shadow={4} p={Spacing['stack.xxxs']} bg="neutral.white">
          {!isLoadingTags && errorTags ? (
            <Box>
              <ErrorBoundary onRefresh={refresherTags} {...errorTags} />
            </Box>
          ) : isLoadingTags ? (
            <Box w="232px" flex={1}>
              <SkeletonTags />
            </Box>
          ) : (
            <ItemTagContent {...tagContentProps} />
          )}
        </Popover.Content>
      </Popover>
    );
  }

  return (
    <Box>
      <ItemTagTriggerButton onOpen={onOpen} tags={data} isListing={isListing} />
      <Actionsheet useRNModal isOpen={isOpen} onClose={onClose} size="full">
        <Actionsheet.Content _dragIndicator={{ bg: 'neutral.lightest' }}>
          <VStack space={Spacing['stack.xxxs']} w="full" p={Spacing['stack.xxxs']}>
            <HStack alignItems="center" justifyContent="space-between">
              <Text variant="semibold" fontSize={Typography['fontsize.lg']} color="neutral.black">
                Hashtags
              </Text>
              <IconButton
                icon="Close"
                iconSize="xxs"
                iconColor={Color['neutral.medium']}
                onPress={onClose}
                testId="icon-button-close-action-sheet"
              />
            </HStack>
            {!isLoadingTags && errorTags ? (
              <Box>
                <ErrorBoundary onRefresh={refresherTags} {...errorTags} />
              </Box>
            ) : isLoadingTags ? (
              <SkeletonTags />
            ) : (
              <ItemTagContent {...tagContentProps} />
            )}
          </VStack>
        </Actionsheet.Content>
      </Actionsheet>
    </Box>
  );
};
